import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  ClickAwayListener,
  Divider,
  InputAdornment,
  Paper,
  Popper,
  TextField,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";

import { dayjs } from "@thalamos/common";
import { isAdminUser } from "@thalamos/aspire-common";

import { LoggedInUserContext } from "../../Contexts.js";
import { routeFns } from "../../routePaths.js";
import type { DefaultPageProps } from "../defaultProps.js";
import { ApprovedEmailDomainManage } from "./ApprovedEmailDomainManage.js";
import { AuditViewerPage } from "./AuditViewerPage.js";
import { DataPermissionManagementPage } from "./DataPermissionManagementPage.js";
import DataSharingPartnership from "./DataSharingPartnership/DataSharingPartnership.js";
import { EmailRedirectManage } from "./EmailRedirectManage.js";
import { EprUserLinksManagementPage } from "./EprUserLinksManagementPage.js";
import { FhirApiClientManagementPage } from "./FhirApiClients/FhirApiClientManagementPage.js";
import { GenericInstancesManagementPage } from "./GenericInstancesManagementPage.js";
import { IcbManagementPage } from "./IcbManagementPage.js";
import { ListsManagementPage } from "./ListsManagementPage.js";
import { LocalAuthorityPaymentRatesPage } from "./LocalAuthorityPaymentRatesPage.js";
import { LondonCareRecordConfigurationPage } from "./LondonCareRecord/LondonCareRecordConfigurationPage.js";
import { MentalHealthCareProvidersPage } from "./MentalHealthCareProviders/MentalHealthCareProvidersPage.js";
import { OdsConfiguration } from "./OdsConfiguration.js";
import { OrganisationManagementPage } from "./OrganisationManagementPage.js";
import { PatientDetailsPage } from "./PatientDetailsPage.js";
import { PdsConfiguration } from "./PdsConfiguration.js";
import { PlaceOfSafetyManagementPage } from "./PlaceOfSafety/PlaceOfSafetyManagementPage.js";
import { RafConfiguration } from "./RafConfiguration.js";
import { RioInstancesManagementPage } from "./RioInstancesManagementPage.js";
import { S12ApprovalConfiguration } from "./S12ApprovalConfiguration.js";
import { Section12DoctorsManagementPage } from "./Section12DoctorsManagementPage.js";
import { Section12LanguagesManagementPage } from "./Section12LanguagesManagementPage.js";
import { Section12PaymentRequestsManagementPage } from "./Section12PaymentRequestsManagementPage.js";
import { Section12SpecialismsManagementPage } from "./Section12SpecialismsManagementPage.js";
import { SingleUseActionTokenPage } from "./SingleUseActionTokenPage/SingleUseActionTokenPage.js";
import { SupersetDashboardsManagementPage } from "./SupersetDashboardsManagementPage.js";
import { TeamManagementPage } from "./TeamManagementPage.js";
import { TeamMergePage } from "./TeamMergePage.js";
import { TermsAndConditionsManagementPage } from "./TermsAndConditionsManagementPage.js";
import { TppInstancesManagementPage } from "./TppInstancesManagementPage.js";
import { TrustManagementPage } from "./Trusts/TrustsPage.js";
import { UserManagementPage } from "./UserManagementPage.js";
import { XeroConfiguration } from "./XeroConfiguration.js";

// Menu item type definitions
interface MenuItem {
  id: string;
  label: string;
  route: string;
  keywords: string[];
  component: ReactNode;
}

interface MenuGroup {
  id: string;
  label: string;
  items: MenuItem[];
}

// Define menu structure with search keywords
const menuStructure: MenuGroup[] = [
  {
    id: "core",
    label: "Core",
    items: [
      {
        id: "users",
        label: "Users",
        route: "adminUsersPage",
        keywords: [
          "users",
          "accounts",
          "people",
          "staff",
          "employees",
          "members",
          "permissions",
          "roles",
        ],
        component: <UserManagementPage />,
      },
      {
        id: "organisations",
        label: "Organisations",
        route: "adminOrganisationsPage",
        keywords: [
          "organisations",
          "organizations",
          "orgs",
          "trusts",
          "nhs",
          "config",
          "settings",
        ],
        component: <OrganisationManagementPage />,
      },
      {
        id: "teams",
        label: "Teams",
        route: "adminTeamsPage",
        keywords: [
          "teams",
          "groups",
          "amhp",
          "doctors",
          "wards",
          "departments",
        ],
        component: <TeamManagementPage />,
      },
      {
        id: "data-sharing",
        label: "Data Sharing Partnerships",
        route: "dataSharingPartershipPage",
        keywords: [
          "data sharing",
          "partnerships",
          "sharing",
          "partners",
          "integration",
          "access",
        ],
        component: <DataSharingPartnership />,
      },
      {
        id: "email-domains",
        label: "Approved Email Domains",
        route: "adminApprovedEmailDomains",
        keywords: [
          "email",
          "domains",
          "approved",
          "whitelist",
          "registration",
          "nhs.net",
        ],
        component: <ApprovedEmailDomainManage />,
      },
      {
        id: "email-redirects",
        label: "Email Redirects",
        route: "adminEmailRedirects",
        keywords: [
          "email",
          "redirect",
          "forwarding",
          "blocked",
          "merge",
          "team",
        ],
        component: <EmailRedirectManage />,
      },
      {
        id: "team-merge",
        label: "Merge Teams",
        route: "adminTeamMerge",
        keywords: [
          "merge",
          "teams",
          "consolidate",
          "combine",
          "transfer",
          "mhl",
        ],
        component: <TeamMergePage />,
      },
      {
        id: "terms",
        label: "Terms & Conditions",
        route: "termsAndConditionsManagementPage",
        keywords: [
          "terms",
          "conditions",
          "legal",
          "agreement",
          "policy",
          "consent",
        ],
        component: <TermsAndConditionsManagementPage />,
      },
    ],
  },
  {
    id: "audit",
    label: "Audit",
    items: [
      {
        id: "audit-viewer",
        label: "Audit Trail Viewer",
        route: "adminAuditTrailResourcePage",
        keywords: [
          "audit",
          "trail",
          "logs",
          "history",
          "activity",
          "tracking",
          "events",
        ],
        component: <AuditViewerPage />,
      },
    ],
  },
  {
    id: "data-management",
    label: "Data Management",
    items: [
      {
        id: "data-permissions",
        label: "Data Permission Management",
        route: "adminDataPermissionsPage",
        keywords: [
          "data",
          "permissions",
          "work items",
          "form context",
          "patient",
          "data controller",
          "delete",
          "remove",
          "cleanup",
        ],
        component: <DataPermissionManagementPage />,
      },
      {
        id: "patient-details",
        label: "Patient details",
        route: "adminPatientDetailsPage",
        keywords: [
          "patient",
          "details",
          "pds",
          "gp",
          "practice",
          "postcode",
          "local authority",
          "la",
          "merge",
          "root",
          "restricted",
        ],
        component: <PatientDetailsPage />,
      },
    ],
  },
  {
    id: "locations",
    label: "Locations",
    items: [
      {
        id: "trusts",
        label: "Trusts",
        route: "adminTrustsPage",
        keywords: ["trusts", "nhs trusts", "providers", "healthcare"],
        component: <TrustManagementPage />,
      },
      {
        id: "mental-health-care-providers",
        label: "Mental Health Care Providers",
        route: "adminMentalHealthCareProvidersPage",
        keywords: [
          "mental health care providers",
          "providers",
          "care providers",
          "soad",
          "second opinion",
        ],
        component: <MentalHealthCareProvidersPage />,
      },
      {
        id: "places-of-safety",
        label: "Places of Safety / Emergency Departments",
        route: "placeOfSafetyManagementPage",
        keywords: [
          "places of safety",
          "pos",
          "s136",
          "section 136",
          "police",
          "custody",
          "ed",
          "emergency department",
        ],
        component: <PlaceOfSafetyManagementPage />,
      },
      {
        id: "managed-locations",
        label: "Hospitals / Local Authorities",
        route: "adminAuditTrailListsPage",
        keywords: [
          "hospitals",
          "local authorities",
          "locations",
          "sites",
          "wards",
          "managed",
          "la",
        ],
        component: <ListsManagementPage />,
      },
      {
        id: "icbs",
        label: "ICBs (Integrated Care Boards)",
        route: "adminIcbsPage",
        keywords: [
          "icb",
          "icbs",
          "integrated care board",
          "out of area",
          "out-of-area",
          "payment policy",
          "dr finder",
        ],
        component: <IcbManagementPage />,
      },
    ],
  },
  {
    id: "integrations",
    label: "Integrations",
    items: [
      {
        id: "pds",
        label: "PDS Configuration",
        route: "pdsConfiguration",
        keywords: [
          "pds",
          "patient demographic",
          "spine",
          "nhs number",
          "demographics",
        ],
        component: <PdsConfiguration />,
      },
      {
        id: "raf",
        label: "RAF Configuration",
        route: "rafConfiguration",
        keywords: [
          "raf",
          "reasonable adjustments",
          "patient flags",
          "spine",
          "accessibility",
        ],
        component: <RafConfiguration />,
      },
      {
        id: "ods",
        label: "ODS Configuration",
        route: "odsConfiguration",
        keywords: [
          "ods",
          "organisation data service",
          "organization",
          "gp practice",
          "local authority",
          "postcode",
          "fhir",
        ],
        component: <OdsConfiguration />,
      },
      {
        id: "lcr",
        label: "London Care Record",
        route: "londonCareRecordConfigurationPage",
        keywords: [
          "london care record",
          "lcr",
          "care record",
          "london",
          "shared care",
        ],
        component: <LondonCareRecordConfigurationPage />,
      },
      {
        id: "rio",
        label: "Rio Instances",
        route: "adminRioInstancesPage",
        keywords: ["rio", "instances", "epr", "patient record", "integration"],
        component: <RioInstancesManagementPage />,
      },
      {
        id: "tpp",
        label: "TPP Instances",
        route: "adminTppInstancesPage",
        keywords: [
          "tpp",
          "systmone",
          "systm one",
          "instances",
          "epr",
          "integration",
          "aes",
        ],
        component: <TppInstancesManagementPage />,
      },
      {
        id: "generic",
        label: "Generic EPR Instances",
        route: "adminGenericInstancesPage",
        keywords: [
          "generic",
          "epr",
          "instances",
          "clickthrough",
          "click through",
          "integration",
          "hmac",
        ],
        component: <GenericInstancesManagementPage />,
      },
      {
        id: "epr-user-links",
        label: "EPR Remembered Sign-ins",
        route: "adminEprUserLinksPage",
        keywords: [
          "epr",
          "clickthrough",
          "click through",
          "remembered",
          "login",
          "sign-in",
          "login_hint",
          "username",
          "links",
        ],
        component: <EprUserLinksManagementPage />,
      },
      {
        id: "superset-dashboards",
        label: "Superset Dashboards",
        route: "adminSupersetDashboardsPage",
        keywords: ["superset", "dashboards", "embed", "analytics", "reporting"],
        component: <SupersetDashboardsManagementPage />,
      },
      {
        id: "action-tokens",
        label: "Single Use Action Tokens",
        route: "singleUseActionTokensPage",
        keywords: [
          "tokens",
          "migration",
          "single use",
          "links",
          "invitations",
          "csv",
        ],
        component: <SingleUseActionTokenPage />,
      },
      {
        id: "fhir-api-clients",
        label: "FHIR API Clients",
        route: "fhirApiClientsPage",
        keywords: [
          "fhir",
          "api",
          "clients",
          "oauth",
          "oauth2",
          "flag",
          "consent",
          "shared care record",
          "external",
          "integration",
          "keys",
          "jwks",
        ],
        component: <FhirApiClientManagementPage />,
      },
    ],
  },
  {
    id: "section12",
    label: "Section 12",
    items: [
      {
        id: "s12-doctors",
        label: "Doctors",
        route: "adminSection12DoctorsPage",
        keywords: ["doctors", "s12", "section 12", "profiles", "gmc"],
        component: <Section12DoctorsManagementPage />,
      },
      {
        id: "s12-specialisms",
        label: "Specialisms",
        route: "adminSection12SpecialismsPage",
        keywords: [
          "specialisms",
          "specialties",
          "s12",
          "section 12",
          "doctors",
          "approved",
        ],
        component: <Section12SpecialismsManagementPage />,
      },
      {
        id: "s12-languages",
        label: "Languages",
        route: "adminSection12LanguagesPage",
        keywords: ["languages", "spoken", "s12", "section 12", "doctors"],
        component: <Section12LanguagesManagementPage />,
      },
      {
        id: "s12-payments",
        label: "Payment Requests",
        route: "adminSection12PaymentRequestsPage",
        keywords: [
          "payment requests",
          "s12",
          "section 12",
          "invoices",
          "claims",
          "billing",
        ],
        component: <Section12PaymentRequestsManagementPage />,
      },
      {
        id: "s12-la-rates",
        label: "LA Payment Rates",
        route: "adminLocalAuthorityPaymentRatesPage",
        keywords: [
          "la rates",
          "local authority",
          "payment rates",
          "fees",
          "mileage",
          "assessment",
          "bma",
        ],
        component: <LocalAuthorityPaymentRatesPage />,
      },
      {
        id: "s12-approval-config",
        label: "S12 Approval API",
        route: "s12ApprovalConfiguration",
        keywords: [
          "s12",
          "section 12",
          "approval",
          "api",
          "nhsbsa",
          "gmc",
          "configuration",
          "checker",
        ],
        component: <S12ApprovalConfiguration />,
      },
      {
        id: "xero-config",
        label: "Xero Billing",
        route: "xeroConfiguration",
        keywords: [
          "xero",
          "billing",
          "invoices",
          "accounting",
          "payments",
          "auto-bill",
          "configuration",
        ],
        component: <XeroConfiguration />,
      },
    ],
  },
];

// Flatten menu items for search
interface FlatMenuItem extends MenuItem {
  groupLabel: string;
  groupId: string;
}

const flattenMenuItems = (groups: MenuGroup[]): FlatMenuItem[] => {
  const items: FlatMenuItem[] = [];
  groups.forEach((group) => {
    group.items.forEach((item) => {
      items.push({
        ...item,
        groupLabel: group.label,
        groupId: group.id,
      });
    });
  });
  return items;
};

export function AdminPage(_props: DefaultPageProps) {
  const [selectedItemId, setSelectedItemId] = useState<string>("users");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const navigate = useNavigate();
  const userContext = useContext(LoggedInUserContext);
  const anchorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const itemRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  // Callback ref for menu items to enable scroll-into-view
  const setItemRef = useCallback(
    (index: number) => (el: HTMLDivElement | null) => {
      if (el) {
        itemRefs.current.set(index, el);
      } else {
        itemRefs.current.delete(index);
      }
    },
    [],
  );

  const flatMenuItems = useMemo(() => flattenMenuItems(menuStructure), []);

  // Filter items based on search
  const filteredItems = useMemo(() => {
    if (!searchTerm) return flatMenuItems;
    const term = searchTerm.toLowerCase();
    return flatMenuItems.filter(
      (item) =>
        item.label.toLowerCase().includes(term) ||
        item.groupLabel.toLowerCase().includes(term) ||
        item.keywords.some((kw) => kw.includes(term)),
    );
  }, [searchTerm, flatMenuItems]);

  // Group filtered items by category
  const groupedFilteredItems = useMemo(() => {
    return filteredItems.reduce<Record<string, FlatMenuItem[]>>((acc, item) => {
      const group = acc[item.groupLabel] ?? [];
      return { ...acc, [item.groupLabel]: [...group, item] };
    }, {});
  }, [filteredItems]);

  // Auto-highlight first item when menu opens or filtered items change
  useEffect(() => {
    if (isMenuOpen && filteredItems.length > 0) {
      setHighlightedIndex(0);
    } else if (filteredItems.length === 0) {
      setHighlightedIndex(-1);
    }
  }, [isMenuOpen, filteredItems]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0) {
      const el = itemRefs.current.get(highlightedIndex);
      el?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [highlightedIndex]);

  // Global keyboard shortcut (Cmd+K / Ctrl+K) to open the menu
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      // Cmd+K (Mac) or Ctrl+K (Windows/Linux) to toggle menu
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setIsMenuOpen((prev) => {
          if (!prev) {
            // Opening the menu - focus the input
            setTimeout(() => inputRef.current?.focus(), 0);
          } else {
            // Closing the menu - reset state
            setSearchTerm("");
            setHighlightedIndex(-1);
          }
          return !prev;
        });
      }
    };

    document.addEventListener("keydown", handleGlobalKeyDown);
    return () => {
      document.removeEventListener("keydown", handleGlobalKeyDown);
    };
  }, []);

  useEffect(() => {
    if (!isAdminUser(userContext?.user)) {
      navigate(routeFns.home());
    }
  }, [userContext?.user, navigate]);

  const { pathname } = useLocation();

  // Sync selected item with URL
  useEffect(() => {
    const matchedItem = flatMenuItems.find((item) => {
      const routeFn = routeFns[item.route as keyof typeof routeFns];
      if (typeof routeFn === "function") {
        if (item.route === "adminAuditTrailResourcePage") {
          return (
            pathname ===
            routeFns.adminAuditTrailResourcePage({
              resourceId: null,
              type: null,
              userId: null,
              fromDate: null,
              toDate: null,
            })
          );
        }
        return pathname === (routeFn as () => string)();
      }
      return false;
    });
    if (matchedItem) {
      setSelectedItemId(matchedItem.id);
    }
  }, [pathname, flatMenuItems]);

  const handleItemSelect = (item: MenuItem) => {
    setSelectedItemId(item.id);
    setIsMenuOpen(false);
    setSearchTerm("");
    setHighlightedIndex(-1);
    const routeFn = routeFns[item.route as keyof typeof routeFns];
    if (typeof routeFn === "function") {
      if (item.route === "adminAuditTrailResourcePage") {
        navigate(
          routeFns.adminAuditTrailResourcePage({
            resourceId: null,
            type: null,
            userId: null,
            fromDate: dayjs().subtract(30, "days"),
            toDate: dayjs(),
          }),
        );
      } else {
        navigate((routeFn as () => string)());
      }
    }
  };

  const handleKeyDown = (e: ReactKeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev < filteredItems.length - 1 ? prev + 1 : 0,
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev > 0 ? prev - 1 : filteredItems.length - 1,
      );
    } else if (e.key === "Enter" && filteredItems.length > 0) {
      e.preventDefault();
      // Select highlighted item, or first item if none highlighted
      const indexToSelect = highlightedIndex >= 0 ? highlightedIndex : 0;
      const itemToSelect = filteredItems[indexToSelect];
      if (itemToSelect !== undefined) {
        handleItemSelect(itemToSelect);
      }
    } else if (e.key === "Escape") {
      setIsMenuOpen(false);
      setSearchTerm("");
      setHighlightedIndex(-1);
      inputRef.current?.blur();
    }
  };

  const selectedItem = flatMenuItems.find((item) => item.id === selectedItemId);

  return (
    <Box sx={{ width: "100%", minHeight: "calc(100vh - 64px)" }}>
      {/* Navigation Bar */}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
          px: 2,
          py: 1,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            fontWeight: 500,
          }}
        >
          Admin
        </Typography>
        <ChevronRightIcon fontSize="small" color="disabled" />

        {/* Combined Search/Menu Button */}
        <ClickAwayListener
          onClickAway={() => {
            setIsMenuOpen(false);
            setSearchTerm("");
            setHighlightedIndex(-1);
          }}
        >
          <Box
            ref={anchorRef}
            sx={{ position: "relative", flex: 1, maxWidth: 400 }}
          >
            <TextField
              inputRef={inputRef}
              size="small"
              fullWidth
              placeholder={
                isMenuOpen ? "Type to search..." : "Select admin page..."
              }
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                // Don't reset highlightedIndex here - let the useEffect handle it
                // when filteredItems changes
              }}
              onFocus={() => {
                setIsMenuOpen(true);
              }}
              onKeyDown={handleKeyDown}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          cursor: "pointer",
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          bgcolor: "grey.100",
                          "&:hover": { bgcolor: "grey.200" },
                        }}
                        onClick={() => {
                          setIsMenuOpen(!isMenuOpen);
                          if (!isMenuOpen) {
                            inputRef.current?.focus();
                          }
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 500,
                            color: "text.primary",
                            maxWidth: 150,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {selectedItem?.label ?? "Select..."}
                        </Typography>
                        <KeyboardArrowDownIcon
                          fontSize="small"
                          sx={{
                            transform: isMenuOpen ? "rotate(180deg)" : "none",
                            transition: "transform 0.2s",
                          }}
                        />
                      </Box>
                    </InputAdornment>
                  ),
                  sx: {
                    bgcolor: "grey.50",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: isMenuOpen ? "primary.main" : "grey.300",
                    },
                  },
                },
              }}
            />

            {/* Dropdown Menu */}
            <Popper
              open={isMenuOpen}
              anchorEl={anchorRef.current}
              placement="bottom-start"
              style={{ zIndex: 1300, width: anchorRef.current?.offsetWidth }}
            >
              <Paper
                elevation={8}
                sx={{
                  mt: 0.5,
                  maxHeight: 400,
                  overflow: "hidden",
                  border: 1,
                  borderColor: "divider",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box sx={{ overflow: "auto", flex: 1 }}>
                  {filteredItems.length === 0 ? (
                    <Box sx={{ p: 2, textAlign: "center" }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                        }}
                      >
                        No matching pages found
                      </Typography>
                    </Box>
                  ) : (
                    Object.entries(groupedFilteredItems).map(
                      ([groupLabel, items], groupIndex) => (
                        <Box key={groupLabel}>
                          {groupIndex > 0 && <Divider />}
                          <Typography
                            variant="caption"
                            sx={{
                              display: "block",
                              px: 2,
                              py: 1,
                              bgcolor: "grey.50",
                              color: "text.secondary",
                              fontWeight: 600,
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                            }}
                          >
                            {groupLabel}
                          </Typography>
                          {items.map((item) => {
                            const itemIndex = filteredItems.indexOf(item);
                            const isHighlighted =
                              itemIndex === highlightedIndex;
                            const isSelected = item.id === selectedItemId;
                            return (
                              <Box
                                key={item.id}
                                ref={setItemRef(itemIndex)}
                                onClick={() => {
                                  handleItemSelect(item);
                                }}
                                sx={{
                                  px: 2,
                                  py: 1,
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  bgcolor: isHighlighted
                                    ? "action.selected"
                                    : isSelected
                                      ? "grey.100"
                                      : "transparent",
                                  "&:hover": {
                                    bgcolor: isHighlighted
                                      ? "action.selected"
                                      : "grey.50",
                                  },
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: isSelected ? 600 : 400,
                                    color: isSelected
                                      ? "primary.main"
                                      : "text.primary",
                                  }}
                                >
                                  {item.label}
                                </Typography>
                                {isSelected && (
                                  <Box
                                    sx={{
                                      width: 6,
                                      height: 6,
                                      borderRadius: "50%",
                                      bgcolor: "primary.main",
                                    }}
                                  />
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      ),
                    )
                  )}
                </Box>

                {/* Keyboard hint - always visible at bottom */}
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    bgcolor: "grey.100",
                    borderTop: 1,
                    borderColor: "divider",
                    display: "flex",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        bgcolor: "grey.200",
                        px: 0.5,
                        borderRadius: 0.5,
                        fontFamily: "monospace",
                      }}
                    >
                      ⌘K
                    </Box>{" "}
                    Open
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        bgcolor: "grey.200",
                        px: 0.5,
                        borderRadius: 0.5,
                        fontFamily: "monospace",
                      }}
                    >
                      ↑↓
                    </Box>{" "}
                    Navigate
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        bgcolor: "grey.200",
                        px: 0.5,
                        borderRadius: 0.5,
                        fontFamily: "monospace",
                      }}
                    >
                      Enter
                    </Box>{" "}
                    Select
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        bgcolor: "grey.200",
                        px: 0.5,
                        borderRadius: 0.5,
                        fontFamily: "monospace",
                      }}
                    >
                      Esc
                    </Box>{" "}
                    Close
                  </Typography>
                </Box>
              </Paper>
            </Popper>
          </Box>
        </ClickAwayListener>
      </Box>
      {/* Main Content */}
      <Box component="main" sx={{ p: 3 }}>
        {selectedItem?.component}
      </Box>
    </Box>
  );
}
